import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { X, Globe, MessageCircle, Sparkles, Mail } from 'lucide-react';

export const QuickReadingForm = ({ isOpen, onClose, onSubmit }) => {
  const [url, setUrl] = useState('');
  const [question, setQuestion] = useState('');
  const [email, setEmail] = useState('');

  if (!isOpen) return null;

  const canSubmit = question.trim().length > 0;

  const handleSubmit = () => {
    onSubmit({
      websiteUrl: url.trim() || null,
      question: question.trim(),
      email: email.trim() || null,
    });
    setUrl('');
    setQuestion('');
    setEmail('');
  };

  const handleClose = () => {
    setUrl('');
    setQuestion('');
    setEmail('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-[#0a1628]/90 backdrop-blur-md"
        onClick={handleClose}
      />

      <div className={cn(
        "relative w-full max-w-lg",
        "bg-gradient-to-br from-[#1e2a4a] to-[#141c33]",
        "border-2 border-[#d4c4a8]/40 rounded-2xl",
        "shadow-2xl shadow-[#e89b47]/10",
        "animate-scale-in"
      )}>
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#d4c4a8]/60 hover:text-[#d4c4a8] transition-colors z-10"
          data-testid="quick-form-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="h-2 bg-gradient-to-r from-transparent via-[#e89b47] to-transparent" />

        <div className="p-8">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl text-[#e8dcc8] mb-2">
              Start Your Reading
            </h2>
            <p className="font-body text-[#d4c4a8]/60 text-sm">
              Tell us about you, then draw your cards
            </p>
          </div>

          <div className="space-y-6">
            {/* Company URL */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
                <Globe className="w-4 h-4 text-[#e89b47]" />
                Enter your company URL
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://yourcompany.com"
                className={cn(
                  "w-full p-3 rounded-xl border-2 border-[#d4c4a8]/20",
                  "bg-[#0a1628]/50 text-[#e8dcc8] placeholder-[#d4c4a8]/30",
                  "font-body text-sm",
                  "focus:outline-none focus:border-[#e89b47]/50 focus:ring-1 focus:ring-[#e89b47]/30",
                  "transition-all duration-300"
                )}
                data-testid="quick-form-url"
              />
            </div>

            {/* Question */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
                <MessageCircle className="w-4 h-4 text-[#e89b47]" />
                Ask a Question
              </label>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="What do you need clarity on — brand, growth, content, audience, or direction?"
                rows={3}
                className={cn(
                  "w-full p-3 rounded-xl border-2 border-[#d4c4a8]/20",
                  "bg-[#0a1628]/50 text-[#e8dcc8] placeholder-[#d4c4a8]/30",
                  "font-body text-sm resize-none",
                  "focus:outline-none focus:border-[#e89b47]/50 focus:ring-1 focus:ring-[#e89b47]/30",
                  "transition-all duration-300"
                )}
                data-testid="quick-form-question"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
                <Mail className="w-4 h-4 text-[#e89b47]" />
                Enter your email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={cn(
                  "w-full p-3 rounded-xl border-2 border-[#d4c4a8]/20",
                  "bg-[#0a1628]/50 text-[#e8dcc8] placeholder-[#d4c4a8]/30",
                  "font-body text-sm",
                  "focus:outline-none focus:border-[#e89b47]/50 focus:ring-1 focus:ring-[#e89b47]/30",
                  "transition-all duration-300"
                )}
                data-testid="quick-form-email"
              />
            </div>

            {/* CTA */}
            <div className="flex justify-center pt-4">
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={cn(
                  "font-display tracking-wide px-10 py-6 text-lg transition-all",
                  canSubmit
                    ? "bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] hover:from-[#e89b47]/90 hover:to-[#d4883a]/90 shadow-lg shadow-[#e89b47]/20"
                    : "bg-[#d4c4a8]/20 text-[#d4c4a8]/50 cursor-not-allowed"
                )}
                data-testid="quick-form-submit"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Draw Your Card
              </Button>
            </div>
          </div>
        </div>

        <div className="h-1 bg-gradient-to-r from-transparent via-[#e89b47]/50 to-transparent" />
      </div>
    </div>
  );
};

export default QuickReadingForm;
